import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetIdentity, useNotify, useTranslate } from 'react-admin';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  BB_BOX_TYPES,
  BB_DESIGNATED_AMOUNT_CAP,
  BB_GRANT_REASONS,
  BB_REVOKE_MAX_IDS,
  BbBoxType,
  BbGrantBoxInput,
  BbGrantReason,
  BbManualDistribution,
  BbManualDistributionBox,
  BbManualDistributionQuery,
  BbManualDistributionStats,
  bbBatchGrantBox,
  bbForceOpenManualDistribution,
  bbGetManualDistributionBoxes,
  bbGetManualDistributionStats,
  bbGetManualDistributions,
  bbGrantBox,
} from '../../../services/blindBoxService';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import { downloadTemplate, exportRowsXlsx, parseSheetRecords } from './xlsx';
import { RevokeDialog } from './RevokeDialog';
import { decimalToNum, isRevoked, revokeActionFor, shortfallOf } from './revokeHelpers';

// ── i18n helpers ──────────────────────────────────────────────────────────────

const BOX_TYPE_I18N: Record<BbBoxType, string> = {
  NORMAL: 'module.blindBox.mdBoxTypeNormal',
  DESIGNATED_AMOUNT: 'module.blindBox.mdBoxTypeDesignated',
};

const REASON_I18N: Record<BbGrantReason, string> = {
  ACTIVITY_REWARD: 'module.blindBox.mdReasonActivityReward',
  CS_REISSUE: 'module.blindBox.mdReasonCsReissue',
  RISK_COMPENSATION: 'module.blindBox.mdReasonRiskCompensation',
  MANUAL_OPERATION: 'module.blindBox.mdReasonManualOperation',
  OTHER: 'module.blindBox.mdReasonOther',
};

const NORMAL_QUANTITY_MIN = 1;
const NORMAL_QUANTITY_MAX = 100;

const shortWallet = (w?: string | null) => {
  const s = String(w ?? '');
  if (!s) return '';
  return s.length <= 12 ? s : `${s.slice(0, 6)}...${s.slice(-4)}`;
};

const toNum = (v: string | number | null | undefined): number => {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').trim());
  return Number.isFinite(n) ? n : 0;
};

/** Compact thousands-separated amount, up to 2 decimals (e.g. "5,320.5"). */
const fmtAmount = (n: number): string => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

// ── Grant row form model ──────────────────────────────────────────────────────

type GrantRowForm = {
  userId: string;
  walletAddress: string;
  boxType: BbBoxType;
  quantity: string;
  amount: string;
  reason: BbGrantReason;
  notes: string;
};

const emptyRow = (boxType: BbBoxType = 'NORMAL'): GrantRowForm => ({
  userId: '',
  walletAddress: '',
  boxType,
  quantity: '1',
  amount: '',
  reason: 'ACTIVITY_REWARD',
  notes: '',
});

/** Client-side validation mirroring the backend row rules; returns an i18n key or null. */
function validateRow(row: GrantRowForm): string | null {
  if (!row.userId.trim() && !row.walletAddress.trim()) {
    return 'module.blindBox.mdErrMissingIdentifier';
  }
  const qty = Number(row.quantity);
  if (row.boxType === 'NORMAL') {
    if (!Number.isInteger(qty) || qty < NORMAL_QUANTITY_MIN || qty > NORMAL_QUANTITY_MAX) {
      return 'module.blindBox.mdErrQuantityNormal';
    }
    if (row.amount.trim() !== '') {
      return 'module.blindBox.mdErrAmountOnNormal';
    }
  } else {
    if (qty !== 1) {
      return 'module.blindBox.mdErrQuantityDesignated';
    }
    const amt = Number(row.amount);
    if (
      row.amount.trim() === '' ||
      !Number.isFinite(amt) ||
      amt <= 0 ||
      amt > BB_DESIGNATED_AMOUNT_CAP
    ) {
      return 'module.blindBox.mdErrAmountDesignated';
    }
  }
  return null;
}

/** Turns a validated row + operator into the API payload (omits amount for NORMAL). */
function toGrantInput(row: GrantRowForm, operator: string): BbGrantBoxInput {
  const base: BbGrantBoxInput = {
    boxType: row.boxType,
    quantity: row.boxType === 'DESIGNATED_AMOUNT' ? 1 : Number(row.quantity),
    reason: row.reason,
    operator,
  };
  if (row.userId.trim()) base.userId = row.userId.trim();
  else if (row.walletAddress.trim()) base.walletAddress = row.walletAddress.trim();
  if (row.notes.trim()) base.notes = row.notes.trim();
  if (row.boxType === 'DESIGNATED_AMOUNT') base.amount = row.amount.trim();
  return base;
}

// ── Stat cards ────────────────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  unit,
  subtitle,
}: {
  title: string;
  value: string | number;
  unit?: string;
  subtitle?: string;
}) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" sx={{ mt: 1 }}>
          {value}
          {unit ? (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              {unit}
            </Typography>
          ) : null}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ── Status chip (derived from openedCount / quantity) ─────────────────────────

function StatusChip({ record }: { record: BbManualDistribution }) {
  const t = useTranslate();
  const opened = record.openedCount ?? 0;
  const total = record.quantity ?? 0;
  const fully = total > 0 && opened >= total;
  if (isRevoked(record)) {
    const tip = [
      record.revokedAt ? dayjs(record.revokedAt).format('YYYY-MM-DD HH:mm:ss') : '',
      record.revokedBy || '',
      record.revokeReason || '',
    ]
      .filter(Boolean)
      .join(' · ');
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title={tip} arrow>
          <Chip size="small" color="error" label={t('module.blindBox.mdStatusRevoked')} />
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          {opened}/{total}
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Chip
        size="small"
        color={fully ? 'success' : 'default'}
        label={t(fully ? 'module.blindBox.mdStatusOpened' : 'module.blindBox.mdStatusPending')}
      />
      <Typography variant="caption" color="text.secondary">
        {opened}/{total}
      </Typography>
    </Stack>
  );
}

/** Per-box status chip for the expanded detail table (PENDING / CONSUMED / EXPIRED / REVOKED). */
function BoxStatusChip({ status }: { status: string }) {
  const color: 'success' | 'default' | 'warning' | 'error' =
    status === 'CONSUMED'
      ? 'success'
      : status === 'EXPIRED'
        ? 'warning'
        : status === 'REVOKED'
          ? 'error'
          : 'default';
  return <Chip size="small" variant="outlined" color={color} label={status || '—'} />;
}

/** Expanded per-box detail table for one distribution record (section 5). */
function BoxDetailTable({ boxes }: { boxes: BbManualDistributionBox[] }) {
  const t = useTranslate();
  if (!boxes.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {t('module.blindBox.mdNoRecords')}
      </Typography>
    );
  }
  return (
    <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 48 }}>#</TableCell>
          <TableCell>{t('module.blindBox.mdColRewardCategory')}</TableCell>
          <TableCell>{t('module.blindBox.mdColRewardName')}</TableCell>
          <TableCell align="right">{t('module.blindBox.mdColAmount')}</TableCell>
          <TableCell>{t('module.blindBox.mdColBoxStatus')}</TableCell>
          <TableCell>{t('module.blindBox.mdColOpenedAt')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {boxes.map((b, idx) => (
          <TableRow key={b.pendingId || idx}>
            <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>
            <TableCell>{b.rewardCategory || '—'}</TableCell>
            <TableCell>{b.rewardName || '—'}</TableCell>
            <TableCell align="right">
              {b.rewardAmount !== undefined && b.rewardAmount !== null && b.rewardAmount !== ''
                ? String(b.rewardAmount)
                : '—'}
            </TableCell>
            <TableCell>
              <BoxStatusChip status={String(b.status ?? '')} />
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {b.openedAt ? dayjs(b.openedAt).format('YYYY-MM-DD HH:mm:ss') : '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ── Single-grant form ─────────────────────────────────────────────────────────

function SingleGrantForm({
  boxType,
  operator,
  submitting,
  onSubmit,
}: {
  boxType: BbBoxType;
  operator: string;
  submitting: boolean;
  onSubmit: (input: BbGrantBoxInput) => void;
}) {
  const t = useTranslate();
  const notify = useNotify();
  // Seed from the box type chosen in the parent toggle (parent also remounts via key on change).
  const [row, setRow] = useState<GrantRowForm>(() => emptyRow(boxType));

  const set = (patch: Partial<GrantRowForm>) => setRow((p) => ({ ...p, ...patch }));

  const submit = () => {
    const err = validateRow(row);
    if (err) {
      notify(t(err), { type: 'warning' });
      return;
    }
    if (!operator.trim()) {
      notify(t('module.blindBox.mdErrMissingOperator'), { type: 'warning' });
      return;
    }
    onSubmit(toGrantInput(row, operator.trim()));
    setRow(emptyRow(row.boxType));
  };

  const isDesignated = row.boxType === 'DESIGNATED_AMOUNT';

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label={t('module.blindBox.mdUserId')}
            value={row.userId}
            onChange={(e) => set({ userId: e.target.value })}
            fullWidth
            size="small"
            placeholder="uid / UUID"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={t('module.blindBox.mdWalletAddress')}
            value={row.walletAddress}
            onChange={(e) => set({ walletAddress: e.target.value })}
            fullWidth
            size="small"
            placeholder="0x + 40 hex"
          />
        </Grid>
      </Grid>
      <Typography variant="caption" color="text.secondary">
        {t('module.blindBox.mdIdentifierHint')}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={isDesignated ? 4 : 6}>
          <TextField
            type="number"
            label={t('module.blindBox.mdQuantity')}
            value={isDesignated ? 1 : row.quantity}
            onChange={(e) => set({ quantity: e.target.value })}
            disabled={isDesignated}
            fullWidth
            size="small"
            inputProps={{ min: NORMAL_QUANTITY_MIN, max: NORMAL_QUANTITY_MAX }}
            helperText={
              isDesignated
                ? t('module.blindBox.mdQuantityDesignatedHint')
                : t('module.blindBox.mdQuantityNormalHint')
            }
          />
        </Grid>
        {isDesignated ? (
          <Grid item xs={12} md={4}>
            <TextField
              label={t('module.blindBox.mdAmount')}
              value={row.amount}
              onChange={(e) => set({ amount: e.target.value })}
              fullWidth
              size="small"
              placeholder="USDC"
              helperText={t('module.blindBox.mdAmountHint', { cap: BB_DESIGNATED_AMOUNT_CAP })}
            />
          </Grid>
        ) : null}
        <Grid item xs={12} md={isDesignated ? 4 : 6}>
          <TextField
            select
            label={t('module.blindBox.mdReason')}
            value={row.reason}
            onChange={(e) => set({ reason: e.target.value as BbGrantReason })}
            fullWidth
            size="small"
          >
            {BB_GRANT_REASONS.map((r) => (
              <MenuItem key={r} value={r}>
                {t(REASON_I18N[r])}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <TextField
        label={t('module.blindBox.mdNotes')}
        value={row.notes}
        onChange={(e) => set({ notes: e.target.value })}
        fullWidth
        size="small"
        multiline
        minRows={3}
        placeholder={t('module.blindBox.mdNotesPlaceholder')}
      />

      <Box>
        <PermissionButton resource="bb-manual-distribution" action="create">
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {t('module.blindBox.mdConfirmIssuance')}
          </Button>
        </PermissionButton>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          {t('module.blindBox.mdConfirmIssuanceHint')}
        </Typography>
      </Box>
    </Stack>
  );
}

// ── Bulk-grant form ───────────────────────────────────────────────────────────
// Box type (from the parent toggle), reason, and notes are shared across every row; each row only
// carries the per-recipient wallet + quantity (+ amount for DESIGNATED_AMOUNT). This matches the
// CSV template, which has just those columns.

type BulkRow = { walletAddress: string; quantity: string; amount: string };

const emptyBulkRow = (): BulkRow => ({ walletAddress: '', quantity: '1', amount: '' });

const isBlankBulkRow = (r: BulkRow) => !r.walletAddress.trim() && !r.amount.trim();

/** Per-row validation for bulk mode; returns an i18n key or null. */
function validateBulkRow(row: BulkRow, boxType: BbBoxType): string | null {
  if (!row.walletAddress.trim()) return 'module.blindBox.mdErrMissingWallet';
  if (boxType === 'DESIGNATED_AMOUNT') {
    // Designated rows are always one box; only the amount is entered.
    const amt = Number(row.amount);
    if (
      row.amount.trim() === '' ||
      !Number.isFinite(amt) ||
      amt <= 0 ||
      amt > BB_DESIGNATED_AMOUNT_CAP
    ) {
      return 'module.blindBox.mdErrAmountDesignated';
    }
    return null;
  }
  const qty = Number(row.quantity);
  if (!Number.isInteger(qty) || qty < 1) return 'module.blindBox.mdErrQuantityPositive';
  if (qty > NORMAL_QUANTITY_MAX) return 'module.blindBox.mdErrQuantityNormal';
  return null;
}

/**
 * Turn rows into API inputs. NORMAL carries its quantity on a single grant; DESIGNATED_AMOUNT is
 * always exactly one box per row (submit several rows to give one wallet several designated boxes).
 */
function buildBulkInputs(
  rows: BulkRow[],
  boxType: BbBoxType,
  reason: BbGrantReason,
  notes: string,
  operator: string
): BbGrantBoxInput[] {
  const trimmedNotes = notes.trim();
  return rows.map((row) => {
    const input: BbGrantBoxInput = {
      boxType,
      quantity: boxType === 'DESIGNATED_AMOUNT' ? 1 : Number(row.quantity),
      reason,
      operator,
      walletAddress: row.walletAddress.trim(),
    };
    if (trimmedNotes) input.notes = trimmedNotes;
    if (boxType === 'DESIGNATED_AMOUNT') input.amount = row.amount.trim();
    return input;
  });
}

function BulkGrantForm({
  boxType,
  operator,
  submitting,
  onSubmit,
}: {
  boxType: BbBoxType;
  operator: string;
  submitting: boolean;
  onSubmit: (inputs: BbGrantBoxInput[]) => Promise<boolean>;
}) {
  const t = useTranslate();
  const notify = useNotify();
  const [reason, setReason] = useState<BbGrantReason>('ACTIVITY_REWARD');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<BulkRow[]>([emptyBulkRow()]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDesignated = boxType === 'DESIGNATED_AMOUNT';

  const setRow = (idx: number, patch: Partial<BulkRow>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addRow = () => setRows((prev) => [...prev, emptyBulkRow()]);
  const removeRow = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));

  // One grant entry per row (the batch cap is on entries): DESIGNATED_AMOUNT is one box per row,
  // NORMAL grants its quantity within a single entry.
  const plannedCount = rows.length;

  const handleImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so re-selecting the same file fires onChange again.
    e.target.value = '';
    if (!file) return;
    try {
      const buffer = await file.arrayBuffer();
      const records = await parseSheetRecords(buffer);
      const imported: BulkRow[] = records.map((rec) => ({
        walletAddress: rec.walletAddress,
        // Designated sheet has no quantity column — always one box per row.
        quantity: isDesignated ? '1' : rec.quantity || '1',
        amount: isDesignated ? rec.amount : '',
      }));
      if (!imported.length) {
        notify(t('module.blindBox.mdCsvEmpty'), { type: 'warning' });
        return;
      }
      setRows((prev) => {
        // Drop the leading placeholder row, then append the imported rows (capped at 1000).
        const kept = prev.filter((r) => !isBlankBulkRow(r));
        return [...kept, ...imported].slice(0, 1000);
      });
      if (imported.length > 1000) {
        notify(t('module.blindBox.mdCsvTruncated'), { type: 'warning' });
      } else {
        notify(t('module.blindBox.mdCsvImported', { count: imported.length }), { type: 'info' });
      }
    } catch {
      notify(t('module.blindBox.mdCsvParseError'), { type: 'error' });
    }
  };

  const submit = async () => {
    if (!operator.trim()) {
      notify(t('module.blindBox.mdErrMissingOperator'), { type: 'warning' });
      return;
    }
    if (!rows.length) {
      notify(t('module.blindBox.mdErrBatchSize'), { type: 'warning' });
      return;
    }
    const firstBad = rows.findIndex((r) => validateBulkRow(r, boxType) !== null);
    if (firstBad >= 0) {
      notify(`#${firstBad + 1}: ${t(validateBulkRow(rows[firstBad], boxType)!)}`, {
        type: 'warning',
      });
      return;
    }
    const inputs = buildBulkInputs(rows, boxType, reason, notes, operator.trim());
    if (inputs.length < 1 || inputs.length > 1000) {
      notify(t('module.blindBox.mdErrBatchSize'), { type: 'warning' });
      return;
    }
    // Clear the rows only when the batch persisted; on failure keep them so they can be fixed.
    const ok = await onSubmit(inputs);
    if (ok) {
      setRows([emptyBulkRow()]);
      setNotes('');
    }
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        {t(isDesignated ? 'module.blindBox.mdBulkHintDesignated' : 'module.blindBox.mdBulkHint')}
      </Alert>

      {/* Shared settings applied to every row */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label={t('module.blindBox.mdReason')}
          value={reason}
          onChange={(e) => setReason(e.target.value as BbGrantReason)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          {BB_GRANT_REASONS.map((r) => (
            <MenuItem key={r} value={r}>
              {t(REASON_I18N[r])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('module.blindBox.mdNotes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          size="small"
          fullWidth
          placeholder={t('module.blindBox.mdNotesPlaceholder')}
        />
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }}>#</TableCell>
              <TableCell sx={{ minWidth: 280 }}>{t('module.blindBox.mdWalletAddress')}</TableCell>
              {isDesignated ? (
                <TableCell sx={{ width: 200 }}>{t('module.blindBox.mdAmount')}</TableCell>
              ) : (
                <TableCell sx={{ width: 200 }}>{t('module.blindBox.mdQuantity')}</TableCell>
              )}
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={idx}>
                <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>
                <TableCell>
                  <TextField
                    value={r.walletAddress}
                    onChange={(e) => setRow(idx, { walletAddress: e.target.value })}
                    size="small"
                    variant="standard"
                    placeholder="0x + 40 hex"
                    fullWidth
                  />
                </TableCell>
                {isDesignated ? (
                  <TableCell>
                    <TextField
                      value={r.amount}
                      onChange={(e) => setRow(idx, { amount: e.target.value })}
                      size="small"
                      variant="standard"
                      placeholder="USDC"
                      fullWidth
                    />
                  </TableCell>
                ) : (
                  <TableCell>
                    <TextField
                      type="number"
                      value={r.quantity}
                      onChange={(e) => setRow(idx, { quantity: e.target.value })}
                      size="small"
                      variant="standard"
                      inputProps={{ min: 1, max: NORMAL_QUANTITY_MAX }}
                      fullWidth
                    />
                  </TableCell>
                )}
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={rows.length <= 1}
                    onClick={() => removeRow(idx)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button size="small" startIcon={<AddIcon />} onClick={addRow}>
          {t('module.blindBox.mdAddRow')}
        </Button>
        <Button
          size="small"
          variant="text"
          startIcon={<DownloadIcon />}
          onClick={() =>
            downloadTemplate(boxType).catch(() =>
              notify(t('module.blindBox.mdCsvParseError'), { type: 'error' })
            )
          }
        >
          {t('module.blindBox.mdDownloadTemplate')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          {t('module.blindBox.mdImportCsv')}
        </Button>
        {/* Hidden native input; the outlined button above proxies its click. */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          hidden
          onChange={handleImportFile}
        />
        <Box sx={{ flex: 1 }} />
        <PermissionButton resource="bb-manual-distribution" action="create">
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {t('module.blindBox.mdConfirmBatch', { count: plannedCount })}
          </Button>
        </PermissionButton>
      </Stack>
    </Stack>
  );
}

// ── Recent records panel ──────────────────────────────────────────────────────

function RecentRecords({ items }: { items: BbManualDistribution[] }) {
  const t = useTranslate();
  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('module.blindBox.mdNoRecords')}
      </Typography>
    );
  }
  return (
    <Stack spacing={1.5}>
      {items.map((r) => (
        <Card key={r.id} variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {r.userId || shortWallet(r.walletAddress) || '—'}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={t(BOX_TYPE_I18N[r.boxType] ?? 'module.blindBox.mdBoxTypeNormal')}
                  />
                  <StatusChip record={r} />
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {t(REASON_I18N[r.reason as BbGrantReason] ?? 'module.blindBox.mdReasonOther')}
                  {r.notes ? ` · ${r.notes}` : ''}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap', pl: 1 }}>
                <Typography variant="subtitle2">×{r.quantity}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.designatedAmount ? `${r.designatedAmount} USDC` : '—'}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────

export function SectionManualDistribution({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const { data: identity } = useGetIdentity();

  const [boxType, setBoxType] = useState<BbBoxType>('NORMAL');
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [operator, setOperator] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Default the operator to the signed-in admin; stays editable.
  useEffect(() => {
    if (operator) return;
    const name =
      (identity as any)?.username || (identity as any)?.name || (identity as any)?.email || '';
    if (name) setOperator(String(name));
  }, [identity, operator]);

  // Dashboard stats (all-time totals; todayBoxes is always "today" server-side).
  const [stats, setStats] = useState<BbManualDistributionStats | null>(null);
  const loadStats = useCallback(() => {
    bbGetManualDistributionStats()
      .then(setStats)
      .catch(() => setStats(null));
  }, []);
  useEffect(() => {
    loadStats();
  }, [loadStats, refreshKey]);

  // History table state
  const [rows, setRows] = useState<BbManualDistribution[]>([]);
  // "Recent" list is independent of the table's filters/pagination: always the latest few.
  const [recent, setRecent] = useState<BbManualDistribution[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [fUserId, setFUserId] = useState('');
  const [fWallet, setFWallet] = useState('');
  const [fBoxType, setFBoxType] = useState<'' | BbBoxType>('');
  const [fReason, setFReason] = useState<'' | BbGrantReason>('');
  const [fOperator, setFOperator] = useState('');
  const [fFrom, setFFrom] = useState<Dayjs | null>(null);
  const [fTo, setFTo] = useState<Dayjs | null>(null);
  const [fRevoked, setFRevoked] = useState<'' | 'revoked' | 'notRevoked'>('');
  const revokedFilter = fRevoked === '' ? undefined : fRevoked === 'revoked';

  const load = useCallback(() => {
    bbGetManualDistributions({
      userId: fUserId.trim() || undefined,
      walletAddress: fWallet.trim() || undefined,
      boxType: fBoxType || undefined,
      reason: fReason || undefined,
      operator: fOperator.trim() || undefined,
      // Date-only filters: cover the whole day so the range is inclusive on both ends.
      fromDate: fFrom?.startOf('day').toISOString() || undefined,
      toDate: fTo?.endOf('day').toISOString() || undefined,
      revoked: revokedFilter,
      page: page + 1,
      pageSize,
    })
      .then((r) => {
        setRows(r.items || []);
        setTotal(r.total || 0);
      })
      .catch((e: any) => notify(e?.message || 'Error', { type: 'error' }));
  }, [
    fUserId,
    fWallet,
    fBoxType,
    fReason,
    fOperator,
    fFrom,
    fTo,
    revokedFilter,
    page,
    pageSize,
    notify,
  ]);

  // Latest few records, independent of the table's filters/pagination (server sorts newest first).
  const loadRecent = useCallback(() => {
    bbGetManualDistributions({ page: 1, pageSize: 4 })
      .then((r) => setRecent(r.items || []))
      .catch(() => setRecent([]));
  }, []);

  useEffect(() => {
    setPage(0);
  }, [fUserId, fWallet, fBoxType, fReason, fOperator, fFrom, fTo, fRevoked]);

  const hasFilters =
    !!fUserId ||
    !!fWallet ||
    !!fBoxType ||
    !!fReason ||
    !!fOperator ||
    !!fFrom ||
    !!fTo ||
    !!fRevoked;

  const resetFilters = () => {
    // Clearing the filters re-triggers load via the effects above.
    setFUserId('');
    setFWallet('');
    setFBoxType('');
    setFReason('');
    setFOperator('');
    setFFrom(null);
    setFTo(null);
    setFRevoked('');
  };

  // ── Force-open a record's remaining boxes (section 7; one-shot per record) ────
  const [openingId, setOpeningId] = useState<string | null>(null);

  const forceOpen = async (record: BbManualDistribution) => {
    if (!operator.trim()) {
      notify(t('module.blindBox.mdErrMissingOperator'), { type: 'warning' });
      return;
    }
    const remaining = (record.quantity ?? 0) - (record.openedCount ?? 0);
    if (!window.confirm(t('module.blindBox.mdForceOpenConfirm', { count: remaining }))) return;
    setOpeningId(record.id);
    try {
      const res = await bbForceOpenManualDistribution(record.id, operator.trim());
      notify(t('module.blindBox.mdForceOpenOk', { count: res.openedCount }), { type: 'success' });
      load();
      loadStats();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setOpeningId(null);
    }
  };

  // ── Per-box detail expand (section 5; NORMAL records only) ───────────────────
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [boxesByDist, setBoxesByDist] = useState<Record<string, BbManualDistributionBox[]>>({});
  const [boxesLoadingId, setBoxesLoadingId] = useState<string | null>(null);

  // Collapse and drop cached box detail whenever the table data changes (filter/page/force-open).
  useEffect(() => {
    setExpandedId(null);
    setBoxesByDist({});
  }, [rows]);

  // ── Revoke (cancel unopened boxes + claw back opened USDC) ────────────────────
  // Selection is per page: it resets whenever the rows change (filter/page/reload).
  const [selected, setSelected] = useState<string[]>([]);
  const [revokeTarget, setRevokeTarget] = useState<{ ids: string[]; retry: boolean } | null>(
    null
  );

  useEffect(() => {
    setSelected([]);
  }, [rows]);

  const selectableIds = useMemo(
    () => rows.filter((r) => revokeActionFor(r) !== null).map((r) => r.id),
    [rows]
  );
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.includes(id));
  const someSelected = selected.length > 0 && !allSelected;

  const toggleSelect = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleSelectAll = () => setSelected(allSelected ? [] : selectableIds);

  const openBulkRevoke = () => {
    if (selected.length > BB_REVOKE_MAX_IDS) {
      notify(t('module.blindBox.mdRevokeErrTooMany', { max: BB_REVOKE_MAX_IDS }), {
        type: 'warning',
      });
      return;
    }
    const retry = rows.filter((r) => selected.includes(r.id)).every((r) => isRevoked(r));
    setRevokeTarget({ ids: selected, retry });
  };

  const afterRevoke = () => {
    setSelected([]);
    load();
    loadRecent();
    loadStats();
  };

  const toggleExpand = (record: BbManualDistribution) => {
    if (expandedId === record.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(record.id);
    if (boxesByDist[record.id]) return; // already cached
    setBoxesLoadingId(record.id);
    // quantity is capped at 100 per record, so one page returns every box.
    bbGetManualDistributionBoxes({ distributionId: record.id, page: 1, pageSize: 100 })
      .then((res) => setBoxesByDist((prev) => ({ ...prev, [record.id]: res.items || [] })))
      .catch((e: any) => {
        notify(e?.message || 'Error', { type: 'error' });
        setExpandedId((cur) => (cur === record.id ? null : cur));
      })
      .finally(() => setBoxesLoadingId(null));
  };

  // ── CSV export (one row per box, via the per-box endpoint) ───────────────────
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const PAGE_SIZE = 200; // server cap
      const MAX_PAGES = 200; // safety stop: 40k boxes
      // Freeze the upper bound so grants created mid-export cannot shift pages; a chosen "to" date
      // covers the whole day, otherwise cap at "now".
      const frozenTo = (fTo ? fTo.endOf('day') : dayjs()).toISOString();
      const baseFilter: BbManualDistributionQuery = {
        userId: fUserId.trim() || undefined,
        walletAddress: fWallet.trim() || undefined,
        boxType: fBoxType || undefined,
        reason: fReason || undefined,
        operator: fOperator.trim() || undefined,
        fromDate: fFrom?.startOf('day').toISOString() || undefined,
        toDate: frozenTo,
        revoked: revokedFilter,
        pageSize: PAGE_SIZE,
      };

      const first = await bbGetManualDistributionBoxes({ ...baseFilter, page: 1 });
      const total = first.total || 0;
      if (!total) {
        notify(t('module.blindBox.mdExportEmpty'), { type: 'info' });
        return;
      }
      const pageCount = Math.min(Math.ceil(total / PAGE_SIZE), MAX_PAGES);
      const boxes = [...first.items];
      // total is authoritative from page 1; loop the remaining pages.
      for (let p = 2; p <= pageCount; p += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await bbGetManualDistributionBoxes({ ...baseFilter, page: p });
        boxes.push(...res.items);
      }

      const fmtTime = (v?: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '');
      const headers = [
        t('module.blindBox.mdColId'),
        t('module.blindBox.mdColBatchId'),
        t('module.blindBox.mdColTime'),
        t('module.blindBox.mdColType'),
        t('module.blindBox.mdColUserId'),
        t('module.blindBox.mdWalletAddress'),
        t('module.blindBox.mdColBoxStatus'),
        t('module.blindBox.mdColOpenedAt'),
        t('module.blindBox.mdColRewardCategory'),
        t('module.blindBox.mdColRewardName'),
        t('module.blindBox.mdColAmount'),
        t('module.blindBox.mdColReason'),
        t('module.blindBox.mdColNotes'),
        t('module.blindBox.mdColOperator'),
      ];
      const sheetRows = boxes.map((b) => [
        b.distributionId ?? '',
        b.batchId ?? '',
        fmtTime(b.grantedAt),
        t(BOX_TYPE_I18N[b.boxType] ?? 'module.blindBox.mdBoxTypeNormal'),
        b.userId ?? '',
        b.walletAddress ?? '',
        String(b.status ?? ''),
        fmtTime(b.openedAt),
        b.rewardCategory ?? '',
        b.rewardName ?? '',
        String(b.rewardAmount ?? ''),
        t(REASON_I18N[b.reason as BbGrantReason] ?? 'module.blindBox.mdReasonOther'),
        b.notes ?? '',
        b.operator ?? '',
      ]);

      await exportRowsXlsx(
        `blind-box-manual-distribution-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`,
        headers,
        sheetRows
      );

      const truncated = total > pageCount * PAGE_SIZE;
      notify(
        t(truncated ? 'module.blindBox.mdExportTruncated' : 'module.blindBox.mdExportOk', {
          count: boxes.length,
        }),
        { type: truncated ? 'warning' : 'success' }
      );
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    loadRecent();
  }, [loadRecent, refreshKey]);

  const afterGrant = () => {
    setPage(0);
    load();
    loadRecent();
    loadStats();
  };

  const submitSingle = async (input: BbGrantBoxInput) => {
    setSubmitting(true);
    try {
      await bbGrantBox(input);
      notify(t('module.blindBox.mdGrantOk'), { type: 'success' });
      afterGrant();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Returns true only when the whole batch persisted, so the form can clear its rows.
  const submitBatch = async (inputs: BbGrantBoxInput[]): Promise<boolean> => {
    setSubmitting(true);
    try {
      const res = await bbBatchGrantBox(inputs);
      if (res.success) {
        notify(t('module.blindBox.mdBatchOk', { count: res.records.length }), { type: 'success' });
        afterGrant();
        return true;
      }
      // Nothing was persisted; surface each failing row so the whole batch can be fixed at once.
      const detail = res.errors
        .map((er) => `#${er.index + 1} ${er.code}: ${er.message}`)
        .join('\n');
      notify(`${t('module.blindBox.mdBatchFailed')}\n${detail}`, {
        type: 'error',
        multiLine: true,
      });
      return false;
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ mt: 2 }}>
      {/* Stat tiles (GET /manual-distributions/stats). Amount = designated + normal-box cash value. */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <StatCard
            title={t('module.blindBox.mdStatBoxesDistributed')}
            value={stats ? stats.totalBoxes.toLocaleString() : '—'}
            subtitle={
              stats
                ? `${t('module.blindBox.mdStatTodayDelta', { count: stats.todayBoxes })} · ${t(
                    'module.blindBox.mdStatOpened',
                    { count: stats.openedBoxes }
                  )}`
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StatCard
            title={t('module.blindBox.mdStatAmountDistributed')}
            value={
              stats
                ? fmtAmount(toNum(stats.designatedAmountTotal) + toNum(stats.normalCashAmountTotal))
                : '—'
            }
            unit="USDC"
            subtitle={
              stats
                ? t('module.blindBox.mdStatAmountBreakdown', {
                    designated: fmtAmount(toNum(stats.designatedAmountTotal)),
                    normal: fmtAmount(toNum(stats.normalCashAmountTotal)),
                  })
                : t('module.blindBox.mdStatAmountHint')
            }
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Distribution form */}
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6">{t('module.blindBox.mdFormTitle')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('module.blindBox.mdFormHint')}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {t('module.blindBox.mdBoxTypeLabel')}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={boxType}
                    onChange={(_e, v) => v && setBoxType(v)}
                  >
                    {BB_BOX_TYPES.map((b) => (
                      <ToggleButton key={b} value={b}>
                        {t(BOX_TYPE_I18N[b])}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {t('module.blindBox.mdModeLabel')}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={mode}
                    onChange={(_e, v) => v && setMode(v)}
                  >
                    <ToggleButton value="single">{t('module.blindBox.mdModeSingle')}</ToggleButton>
                    <ToggleButton value="bulk">{t('module.blindBox.mdModeBulk')}</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {t('module.blindBox.mdOperator')}
                  </Typography>
                  <TextField
                    value={operator}
                    onChange={(e) => setOperator(e.target.value)}
                    size="small"
                    fullWidth
                    placeholder={t('module.blindBox.mdOperator')}
                    sx={{
                      height: 45,
                      '& .MuiInputBase-root': { minHeight: 45 },
                    }}
                    disabled
                  />
                </Box>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {mode === 'single' ? (
                <SingleGrantForm
                  key={boxType}
                  boxType={boxType}
                  operator={operator}
                  submitting={submitting}
                  onSubmit={submitSingle}
                />
              ) : (
                <BulkGrantForm
                  boxType={boxType}
                  operator={operator}
                  submitting={submitting}
                  onSubmit={submitBatch}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent records */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6">{t('module.blindBox.mdRecentTitle')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('module.blindBox.mdRecentHint')}
              </Typography>
              <RecentRecords items={recent} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Distribution records table */}
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6">{t('module.blindBox.mdRecordsTitle')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('module.blindBox.mdRecordsHint')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
              sx={{ flexShrink: 0 }}
            >
              {t(exporting ? 'module.blindBox.mdExporting' : 'module.blindBox.mdExport')}
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              label={t('module.blindBox.mdUserId')}
              value={fUserId}
              onChange={(e) => setFUserId(e.target.value)}
              sx={{ maxWidth: 180 }}
            />
            <TextField
              size="small"
              label={t('module.blindBox.mdWalletAddress')}
              value={fWallet}
              onChange={(e) => setFWallet(e.target.value)}
              sx={{ maxWidth: 180 }}
            />
            <TextField
              size="small"
              select
              label={t('module.blindBox.mdColType')}
              value={fBoxType}
              onChange={(e) => setFBoxType(e.target.value as '' | BbBoxType)}
              sx={{ minWidth: 278 }}
            >
              <MenuItem value="">{t('module.blindBox.mdAll')}</MenuItem>
              {BB_BOX_TYPES.map((b) => (
                <MenuItem key={b} value={b}>
                  {t(BOX_TYPE_I18N[b])}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label={t('module.blindBox.mdReason')}
              value={fReason}
              onChange={(e) => setFReason(e.target.value as '' | BbGrantReason)}
              sx={{ minWidth: 222 }}
            >
              <MenuItem value="">{t('module.blindBox.mdAll')}</MenuItem>
              {BB_GRANT_REASONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(REASON_I18N[r])}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('module.blindBox.mdOperator')}
              value={fOperator}
              onChange={(e) => setFOperator(e.target.value)}
              sx={{ maxWidth: 120 }}
            />
            <DatePicker
              label={t('module.blindBox.mdFrom')}
              value={fFrom}
              onChange={setFFrom}
              format="YYYY/MM/DD"
              slotProps={{
                field: { clearable: true },
                textField: { size: 'small', sx: { maxWidth: 190 } },
              }}
            />
            <DatePicker
              label={t('module.blindBox.mdTo')}
              value={fTo}
              onChange={setFTo}
              format="YYYY/MM/DD"
              slotProps={{
                field: { clearable: true },
                textField: { size: 'small', sx: { maxWidth: 190 } },
              }}
            />
            <TextField
              size="small"
              select
              label={t('module.blindBox.mdRevokedFilter')}
              value={fRevoked}
              onChange={(e) => setFRevoked(e.target.value as '' | 'revoked' | 'notRevoked')}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">{t('module.blindBox.mdAll')}</MenuItem>
              <MenuItem value="revoked">{t('module.blindBox.mdRevokedOnly')}</MenuItem>
              <MenuItem value="notRevoked">{t('module.blindBox.mdNotRevokedOnly')}</MenuItem>
            </TextField>
            <Button onClick={resetFilters} disabled={!hasFilters}>
              {t('module.blindBox.mdResetFilter')}
            </Button>
          </Stack>

          {selected.length > 0 ? (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{ mb: 2, alignItems: 'center' }}
              action={
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setSelected([])}>
                    {t('module.blindBox.mdClearSelection')}
                  </Button>
                  <PermissionButton resource="bb-manual-distribution" action="revoke">
                    <Button size="small" variant="contained" color="error" onClick={openBulkRevoke}>
                      {t('module.blindBox.mdRevokeSelected', { count: selected.length })}
                    </Button>
                  </PermissionButton>
                </Stack>
              }
            >
              {t('module.blindBox.mdSelectedCount', { count: selected.length })}
            </Alert>
          ) : null}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <PermissionButton resource="bb-manual-distribution" action="revoke">
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        disabled={selectableIds.length === 0}
                        onChange={toggleSelectAll}
                      />
                    </PermissionButton>
                  </TableCell>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell>{t('module.blindBox.mdColId')}</TableCell>
                  <TableCell>{t('module.blindBox.mdColTime')}</TableCell>
                  <TableCell>{t('module.blindBox.mdColType')}</TableCell>
                  <TableCell>{t('module.blindBox.mdColUser')}</TableCell>
                  <TableCell align="right">{t('module.blindBox.mdColQuantity')}</TableCell>
                  <TableCell align="right">{t('module.blindBox.mdColAmount')}</TableCell>
                  <TableCell>{t('module.blindBox.mdColStatus')}</TableCell>
                  <TableCell align="right">{t('module.blindBox.mdColClawback')}</TableCell>
                  <TableCell>{t('module.blindBox.mdColReason')}</TableCell>
                  <TableCell>{t('module.blindBox.mdColNotes')}</TableCell>
                  <TableCell>{t('module.blindBox.mdColOperator')}</TableCell>
                  <TableCell align="right">{t('module.blindBox.mdColOperate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('module.blindBox.mdNoRecords')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => {
                    // Only NORMAL ("Blind box distribution") records expand into per-box detail.
                    const isNormal = r.boxType === 'NORMAL';
                    const open = expandedId === r.id;
                    const revokeAction = revokeActionFor(r);
                    const revoked = isRevoked(r);
                    const shortfall = shortfallOf(r);
                    return (
                      <Fragment key={r.id}>
                        <TableRow hover selected={selected.includes(r.id)}>
                          <TableCell padding="checkbox">
                            {revokeAction ? (
                              <PermissionButton resource="bb-manual-distribution" action="revoke">
                                <Checkbox
                                  size="small"
                                  checked={selected.includes(r.id)}
                                  onChange={() => toggleSelect(r.id)}
                                />
                              </PermissionButton>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ width: 48 }}>
                            {isNormal ? (
                              <IconButton size="small" onClick={() => toggleExpand(r)}>
                                {open ? (
                                  <KeyboardArrowUpIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                )}
                              </IconButton>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            <Tooltip title={r.id} arrow>
                              <span>{shortWallet(r.id)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {r.createdAt ? dayjs(r.createdAt).format('YYYY-MM-DD HH:mm:ss') : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t(
                                BOX_TYPE_I18N[r.boxType] ?? 'module.blindBox.mdBoxTypeNormal'
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 200 }}>
                            <Typography variant="body2" noWrap>
                              {r.userId || '—'}
                            </Typography>
                            {r.walletAddress ? (
                              <Typography variant="caption" color="text.secondary">
                                {r.walletAddress}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">{r.quantity}</TableCell>
                          <TableCell align="right" sx={{ minWidth: 100 }}>
                            {r.designatedAmount ? `${r.designatedAmount} USDC` : '—'}
                          </TableCell>
                          <TableCell>
                            <StatusChip record={r} />
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>
                            {revoked ? (
                              <Box>
                                <Typography variant="body2">
                                  {fmtAmount(decimalToNum(r.clawedBackAmount))} USDC
                                </Typography>
                                {shortfall > 0 ? (
                                  <Typography variant="caption" color="error">
                                    {t('module.blindBox.mdShortfallValue', {
                                      amount: fmtAmount(shortfall),
                                    })}
                                  </Typography>
                                ) : null}
                              </Box>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            {t(
                              REASON_I18N[r.reason as BbGrantReason] ??
                                'module.blindBox.mdReasonOther'
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 240 }}>
                            <Tooltip title={r.notes || ''} arrow>
                              <Typography variant="body2" noWrap>
                                {r.notes || '—'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{r.operator || '—'}</TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                              alignItems="center"
                            >
                              {revoked ? null : r.forceOpenedAt ? (
                                <Tooltip
                                  title={`${t('module.blindBox.mdForceOpened')} · ${dayjs(
                                    r.forceOpenedAt
                                  ).format('YYYY-MM-DD HH:mm:ss')}${
                                    r.forceOpenedBy ? ` · ${r.forceOpenedBy}` : ''
                                  }`}
                                  arrow
                                >
                                  <Chip
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    label={t('module.blindBox.mdForceOpened')}
                                  />
                                </Tooltip>
                              ) : (r.openedCount ?? 0) >= (r.quantity ?? 0) ? (
                                <></>
                              ) : (
                                <PermissionButton
                                  resource="bb-manual-distribution"
                                  action="update"
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={openingId === r.id}
                                    onClick={() => forceOpen(r)}
                                  >
                                    {t('module.blindBox.mdOpen')}
                                  </Button>
                                </PermissionButton>
                              )}
                              {revokeAction ? (
                                <PermissionButton
                                  resource="bb-manual-distribution"
                                  action="revoke"
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color={revokeAction === 'retry' ? 'warning' : 'error'}
                                    sx={{ whiteSpace: 'nowrap' }}
                                    onClick={() =>
                                      setRevokeTarget({
                                        ids: [r.id],
                                        retry: revokeAction === 'retry',
                                      })
                                    }
                                  >
                                    {t(
                                      revokeAction === 'retry'
                                        ? 'module.blindBox.mdRetryClawback'
                                        : 'module.blindBox.mdRevoke'
                                    )}
                                  </Button>
                                </PermissionButton>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                        {isNormal ? (
                          <TableRow>
                            <TableCell
                              colSpan={14}
                              sx={{ py: 0, borderBottom: open ? undefined : 'none' }}
                            >
                              <Collapse in={open} timeout="auto" unmountOnExit>
                                <Box sx={{ m: 1 }}>
                                  {boxesLoadingId === r.id ? (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                      sx={{ py: 1 }}
                                    >
                                      <CircularProgress size={16} />
                                      <Typography variant="body2" color="text.secondary">
                                        {t('common.loading', { _: 'Loading...' })}
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    <BoxDetailTable boxes={boxesByDist[r.id] ?? []} />
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, next) => setPage(next)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setPageSize(Number.isFinite(next) && next > 0 ? next : 20);
              setPage(0);
            }}
            rowsPerPageOptions={[20, 50, 100, 200]}
          />
        </CardContent>
      </Card>

      <RevokeDialog
        open={!!revokeTarget}
        ids={revokeTarget?.ids ?? []}
        retry={revokeTarget?.retry}
        onClose={() => setRevokeTarget(null)}
        onDone={afterRevoke}
      />
    </Stack>
  );
}
